/**
 * Premium Typography Control Components
 *
 * Individual premium typography controls that can be completely replaced by pro plugins
 * using WordPress hooks and filters. Pro plugins can replace the entire component
 * implementation by hooking into the component-level filters.
 *
 * Each component applies a filter to the entire component return, allowing pro plugins
 * to replace them completely with their own implementation.
 *
 * Available Filters:
 * - redfox_thank_you_typography_font_family_control
 * - redfox_thank_you_typography_font_weight_control
 * - redfox_thank_you_typography_letter_spacing_control
 * - redfox_thank_you_typography_word_spacing_control
 * - redfox_thank_you_typography_font_style_control
 * - redfox_thank_you_typography_text_transform_control
 *
 * @package RedfoxThankYou
 * @since 1.0.0
 */

import {
	BaseControl,
	ButtonGroup,
	SelectControl,
} from '@redfox-thank-you/controls';
import { __experimentalUnitControl as UnitControl } from '@wordpress/components';
import { applyFilters } from '@wordpress/hooks';
import { __ } from '@wordpress/i18n';
import React from 'react';

// Import all constants from constants.ts
import {
	DEFAULT_FONT_FAMILY_OPTIONS,
	fontStyleOptions,
	fontWeightOptions,
	textDecorationOptions,
	textTransformOptions,
} from './constants';
import type { TypographyValues } from './types';

/**
 * Interface for individual typography control props
 */
interface TypographyControlComponentProps {
	currentValues: TypographyValues;
	updateAttribute: (
		key: string
	) => ( value: string | number | ( string | number )[] | undefined ) => void;
	resetTypographyProperty: ( property: keyof TypographyValues ) => void;
	hasTypographyValue: ( property: keyof TypographyValues ) => boolean;
	showIndividualResets?: boolean;
}

/**
 * Font Family Control Component
 *
 * @param props - The component props
 * @return JSX element for font family control
 */
const FontFamilyControl: React.FC< TypographyControlComponentProps > = (
	props,
) => {
	const { currentValues } = props;

	// Default component implementation
	const defaultComponent = (
		<BaseControl
			id="typography-font-family"
			label={ __( 'Font Family', 'redfox-thank-you' ) }
			isPro
		>
			<SelectControl
				label=""
				value={ currentValues.fontFamily || '' }
				onChange={ () => {} }
				options={ DEFAULT_FONT_FAMILY_OPTIONS }
				placeholder={ __(
					'Select font family...',
					'redfox-thank-you',
				) }
			/>
		</BaseControl>
	);

	// Apply filter to allow complete component replacement
	return applyFilters(
		'redfox_thank_you_typography_font_family_control',
		defaultComponent,
		props,
	) as JSX.Element;
};

/**
 * Font Weight Control Component
 *
 * @param props - The component props
 * @return JSX element for font weight control
 */
const FontWeightControl: React.FC< TypographyControlComponentProps > = (
	props,
) => {
	const { currentValues } = props;

	// Default component implementation
	const defaultComponent = (
		<BaseControl
			id="typography-font-weight"
			label={ __( 'Font Weight', 'redfox-thank-you' ) }
			isPro
		>
			<SelectControl
				label=""
				value={ currentValues.fontWeight || 'normal' }
				onChange={ () => {} }
				options={ fontWeightOptions }
			/>
		</BaseControl>
	);

	// Apply filter to allow complete component replacement
	return applyFilters(
		'redfox_thank_you_typography_font_weight_control',
		defaultComponent,
		props,
	) as JSX.Element;
};

/**
 * Letter Spacing Control Component
 *
 * @param props - The component props
 * @return JSX element for letter spacing control
 */
const LetterSpacingControl: React.FC< TypographyControlComponentProps > = (
	props,
) => {
	const { currentValues } = props;

	// Default component implementation
	const defaultComponent = (
		<BaseControl
			id="typography-letter-spacing"
			label={ __( 'Letter Spacing', 'redfox-thank-you' ) }
			className="redfox-thank-you-base-control--half-width"
			inline
			isPro
		>
			<UnitControl
				value={ currentValues.letterSpacing }
				onChange={ () => {} }
				units={ [
					{ value: 'px', label: 'px' },
					{ value: 'em', label: 'em' },
					{ value: 'rem', label: 'rem' },
				] }
				min={ -10 }
				max={ 10 }
				step={ 0.1 }
				size="__unstable-large"
				__next40pxDefaultSize
			/>
		</BaseControl>
	);

	// Apply filter to allow complete component replacement
	return applyFilters(
		'redfox_thank_you_typography_letter_spacing_control',
		defaultComponent,
		props,
	) as JSX.Element;
};

/**
 * Word Spacing Control Component
 *
 * @param props - The component props
 * @return JSX element for word spacing control
 */
const WordSpacingControl: React.FC< TypographyControlComponentProps > = (
	props,
) => {
	const { currentValues } = props;

	// Default component implementation
	const defaultComponent = (
		<BaseControl
			id="typography-word-spacing"
			label={ __( 'Word Spacing', 'redfox-thank-you' ) }
			className="redfox-thank-you-base-control--half-width"
			inline
			isPro
		>
			<UnitControl
				value={ currentValues.wordSpacing }
				onChange={ () => {} }
				units={ [
					{ value: 'px', label: 'px' },
					{ value: 'em', label: 'em' },
					{ value: 'rem', label: 'rem' },
				] }
				min={ -10 }
				max={ 50 }
				step={ 0.1 }
				size="__unstable-large"
				__next40pxDefaultSize
			/>
		</BaseControl>
	);

	// Apply filter to allow complete component replacement
	return applyFilters(
		'redfox_thank_you_typography_word_spacing_control',
		defaultComponent,
		props,
	) as JSX.Element;
};

/**
 * Font Style Control Component
 *
 * @param props - The component props
 * @return JSX element for font style control
 */
const FontStyleControl: React.FC< TypographyControlComponentProps > = (
	props,
) => {
	const { currentValues } = props;

	// Default component implementation
	const defaultComponent = (
		<BaseControl
			id="typography-font-style"
			label={ __( 'Font Style', 'redfox-thank-you' ) }
			className="redfox-thank-you-base-control--half-width"
			inline
			isPro
		>
			<div className="redfox-thank-you-typography-control__font-style">
				<ButtonGroup
					value={ currentValues.fontStyle }
					onChange={ () => {} }
					options={ fontStyleOptions }
					allowDeselect={ true }
				/>
				<ButtonGroup
					value={ currentValues.textDecoration }
					onChange={ () => {} }
					options={ textDecorationOptions }
					allowDeselect={ true }
				/>
			</div>
		</BaseControl>
	);

	// Apply filter to allow complete component replacement
	return applyFilters(
		'redfox_thank_you_typography_font_style_control',
		defaultComponent,
		props,
	) as JSX.Element;
};

/**
 * Text Transform Control Component
 *
 * @param props - The component props
 * @return JSX element for text transform control
 */
const TextTransformControl: React.FC< TypographyControlComponentProps > = (
	props,
) => {
	const { currentValues } = props;

	// Default component implementation
	const defaultComponent = (
		<BaseControl
			id="typography-text-transform"
			label={ __( 'Text Transform', 'redfox-thank-you' ) }
			className="redfox-thank-you-base-control--half-width"
			inline
			isPro
		>
			<ButtonGroup
				value={ currentValues.textTransform || 'none' }
				onChange={ () => {} }
				options={ textTransformOptions }
				allowDeselect={ true }
			/>
		</BaseControl>
	);

	// Apply filter to allow complete component replacement
	return applyFilters(
		'redfox_thank_you_typography_text_transform_control',
		defaultComponent,
		props,
	) as JSX.Element;
};

/**
 * Export all premium typography controls
 */
export {
	FontFamilyControl,
	FontStyleControl,
	FontWeightControl,
	LetterSpacingControl,
	TextTransformControl,
	WordSpacingControl,
};

/**
 * Export the props interface for external use
 */
export type { TypographyControlComponentProps };
